import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, MessageCircle, Sparkles, Shield, Users, Star, ArrowRight, 
  Check, Play, MapPin, Clock, Zap, ChevronRight, Eye, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../App';
import { toast } from 'sonner';

const LandingPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Quick signup handler
  const handleQuickSignup = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Indtast din email');
      return;
    }
    // Redirect to auth page with email pre-filled
    navigate('/auth', { state: { email, mode: 'register' } });
  };

  const stats = [
    { value: "50.000+", label: "Aktive brugere" },
    { value: "10.000+", label: "Succesfulde matches" },
    { value: "4.8", label: "App Store rating", icon: <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /> },
  ];

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Smart AI Matchning",
      description: "Vores algoritme finder personer du rent faktisk matcher med"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Lokale Singles",
      description: "Mød singles i dit område - du bestemmer afstanden"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Sikker Dating",
      description: "Verificerede profiler og sikker kommunikation"
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Bryd Isen",
      description: "AI-genererede samtalestartere så du aldrig går i stå"
    }
  ];

  const testimonials = [
    {
      name: "Marie & Jonas",
      location: "København",
      image: "https://images.pexels.com/photos/4873585/pexels-photo-4873585.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      text: "Vi matchede efter kun 3 dage! AI-forslagene hjalp os med at finde hinanden.",
      highlight: "Gift efter 1 år"
    },
    {
      name: "Sofie",
      location: "Aarhus", 
      image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      text: "Endelig en dating app der ikke er fyldt med fake profiler. Super trygt!",
      highlight: "Fandt kærligheden"
    },
    {
      name: "Mikkel & Emma",
      location: "Odense",
      image: "https://images.pexels.com/photos/3978157/pexels-photo-3978157.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      text: "Fra første swipe til forlovelse på 8 måneder. Tak Flirra!",
      highlight: "Forlovet"
    }
  ];

  const steps = [
    { number: "1", title: "Opret profil", description: "Kun 2 minutter - tilføj billeder og bio" },
    { number: "2", title: "Swipe & match", description: "Find lokale singles der matcher dine interesser" },
    { number: "3", title: "Start samtalen", description: "Chat, mød op og find kærligheden" }
  ];

  const trustBadges = [
    { icon: <Lock className="w-4 h-4" />, text: "SSL Krypteret" },
    { icon: <Shield className="w-4 h-4" />, text: "GDPR Compliant" },
    { icon: <Eye className="w-4 h-4" />, text: "Verificerede Profiler" },
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Sticky Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Heart className="w-8 h-8 text-primary fill-primary" />
              <span className="text-2xl font-display font-bold text-foreground">Flirra</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/auth">
                <Button variant="ghost" className="rounded-full hidden sm:inline-flex" data-testid="nav-login-btn">
                  Log ind
                </Button>
              </Link>
              <Link to="/auth">
                <Button className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25" data-testid="nav-signup-btn">
                  Opret gratis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Above the fold */}
      <section className="pt-24 pb-12 sm:pt-32 sm:pb-20 px-4 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center lg:text-left"
            >
              {/* Urgency badge */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Zap className="w-4 h-4" />
                <span>Gratis i 3 dage - ingen binding</span>
              </motion.div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight">
                Find Kærligheden
                <span className="text-gradient block mt-2">I Dit Område</span>
              </h1>
              
              <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0">
                Danmarks smarteste dating app. Mød verificerede singles tæt på dig - 
                vores AI matcher jer baseret på personlighed og interesser.
              </p>

              {/* Quick signup form */}
              <form onSubmit={handleQuickSignup} className="mt-8 max-w-md mx-auto lg:mx-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder="Din email adresse"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 h-14 text-lg rounded-full px-6 border-2 border-border focus:border-primary"
                    data-testid="hero-email-input"
                  />
                  <Button 
                    type="submit"
                    size="lg" 
                    className="h-14 rounded-full bg-primary hover:bg-primary/90 text-lg px-8 shadow-lg shadow-primary/30 whitespace-nowrap"
                    disabled={isLoading}
                    data-testid="hero-cta-btn"
                  >
                    Kom i gang
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 inline mr-1 text-green-500" />
                  Gratis oprettelse • Ingen kreditkort påkrævet
                </p>
              </form>

              {/* Social proof stats */}
              <div className="mt-10 flex flex-wrap justify-center lg:justify-start gap-6 sm:gap-10">
                {stats.map((stat, index) => (
                  <motion.div 
                    key={stat.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="flex items-center justify-center gap-1 text-2xl sm:text-3xl font-display font-bold text-foreground">
                      {stat.value}
                      {stat.icon}
                    </div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: Hero image with floating elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Main image */}
                <img
                  src="https://images.pexels.com/photos/4873585/pexels-photo-4873585.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Lykkeligt par der mødte hinanden på Flirra"
                  className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/5]"
                />
                
                {/* Floating match notification */}
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                  className="absolute -bottom-4 -left-4 bg-card p-4 rounded-2xl shadow-xl border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-green-500 fill-green-500" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground">Nyt Match!</div>
                      <div className="text-xs text-muted-foreground">Emma likede dig også</div>
                    </div>
                  </div>
                </motion.div>

                {/* Floating compatibility badge */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 }}
                  className="absolute -top-4 -right-4 bg-card p-4 rounded-2xl shadow-xl border border-border"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-foreground">95% Match</span>
                  </div>
                </motion.div>

                {/* Distance badge */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute top-1/2 -right-6 bg-primary text-white px-4 py-2 rounded-full shadow-lg"
                >
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <MapPin className="w-4 h-4" />
                    3 km væk
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust badges strip */}
      <section className="py-6 px-4 bg-muted/50 border-y border-border/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 sm:gap-12">
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-muted-foreground">
                {badge.icon}
                <span className="text-sm">{badge.text}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-muted-foreground">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Flag_of_Denmark.svg/23px-Flag_of_Denmark.svg.png" alt="Dansk" className="w-5 h-4" />
              <span className="text-sm">Dansk virksomhed</span>
            </div>
          </div>
        </div>
      </section>

      {/* How it works - 3 steps */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
              Så nemt kommer du i gang
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">
              3 simple trin til dit næste match
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center relative"
              >
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="hidden sm:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-primary/50 to-primary/20" />
                )}
                
                <div className="relative z-10 w-16 h-16 rounded-full bg-primary text-white text-2xl font-display font-bold flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/30">
                  {step.number}
                </div>
                <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* CTA after steps */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/auth">
              <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-lg px-8 py-6 shadow-lg shadow-primary/30" data-testid="steps-cta-btn">
                Start nu - det er gratis
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 sm:py-20 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
              Hvorfor vælge <span className="text-gradient">Flirra</span>?
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">
              Alt hvad du har brug for til at finde den rette
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-2xl border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-display font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Social proof */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
              Rigtige par, rigtig kærlighed
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">
              Tusindvis har allerede fundet deres match
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-2xl border border-border relative"
              >
                {/* Highlight badge */}
                <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {testimonial.highlight}
                </div>
                
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-primary/20"
                  />
                  <div>
                    <div className="font-semibold text-foreground">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {testimonial.location}
                    </div>
                  </div>
                </div>
                <div className="flex text-yellow-400 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground italic">"{testimonial.text}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiM5Qzkyb2YiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAzMHYySDE0di0yaDIyek0zNiAyNnYySDE0di0yaDIyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              <span>Prøv gratis i 3 dage - afmeld når som helst</span>
            </div>
            
            <h2 className="text-3xl sm:text-5xl font-display font-bold text-foreground">
              Klar til at møde den eneste ene?
            </h2>
            <p className="mt-4 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Over 500 nye brugere tilmelder sig hver uge. 
              Bliv en del af Flirra og find din næste store kærlighed.
            </p>

            {/* Signup form */}
            <form onSubmit={handleQuickSignup} className="mt-8 max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="Din email adresse"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 h-14 text-lg rounded-full px-6 border-2 border-border focus:border-primary bg-background"
                  data-testid="final-email-input"
                />
                <Button 
                  type="submit"
                  size="lg" 
                  className="h-14 rounded-full bg-primary hover:bg-primary/90 text-lg px-8 shadow-lg shadow-primary/30 whitespace-nowrap"
                  data-testid="final-cta-btn"
                >
                  Start gratis
                  <Heart className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </form>

            {/* Trust elements */}
            <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Check className="w-4 h-4 text-green-500" />
                100% gratis oprettelse
              </span>
              <span className="flex items-center gap-1">
                <Check className="w-4 h-4 text-green-500" />
                Ingen kreditkort
              </span>
              <span className="flex items-center gap-1">
                <Check className="w-4 h-4 text-green-500" />
                Afmeld når som helst
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary fill-primary" />
              <span className="text-xl font-display font-bold text-foreground">Flirra</span>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-muted-foreground">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privatlivspolitik</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Brugerbetingelser</Link>
              <Link to="/sales-terms" className="hover:text-foreground transition-colors">Handelsbetingelser</Link>
              <a href="mailto:support@flirra.dk" className="hover:text-foreground transition-colors">Kontakt</a>
            </div>
            <div className="text-sm text-muted-foreground">
              © 2026 Flirra. Dansk dating app.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
